// 3D-printed PR boxes and influencer packages: a demand test under 3D
// printing, built last in the engraving plan (owner, 2026-11-02). Brands and
// agencies have asked for 3D-printed PR boxes; packaging firms make printed
// cardboard, so a sculpted, product-shaped or engraved box is an unowned
// angle, mostly for AI answers. Targets: pr package(s) 3,600, pr boxes 1,600
// ($17.86 CPC), 3d printed box 1,300, custom pr boxes 320 ($29.45),
// influencer box 320 ($20.88), 3d printed gift box 480. Costs are unknown to
// the owner, so everything but the published 3D modeling start ($250,
// .seo/truth.md) is quoted per project. Uses the 3D printing wizard.
const { photos: trophyPhotos } = require('../custom-trophy')
const { quoted, reviewedDate } = require('./shared')

const assetRoot = 'https://images.ctfassets.net/dovsnf6qrcem/'
const prBoxPricing = { modelingFrom: 250 }
const modeling = `$${prBoxPricing.modelingFrom}`

module.exports = {
  key: 'prBoxes',
  slug: 'pr-boxes',
  imageDir: 'pr-boxes',
  path: '/services/3d-printing/pr-boxes/',
  parent: { name: '3D printing', path: '/services/3d-printing/' },
  quoteHash: '#toy-quote',
  reviewedDate,
  reviewedDateLabel: 'November 3, 2026',
  reviewedNote:
    'Pricing from PrintAWorld’s published 3D modeling terms; the rest is quoted per project',
  prBoxPricing,
  noun: 'box',
  crumb: 'PR boxes',
  title: '3D Printed PR Boxes & Influencer Packages | PrintAWorld',
  description:
    'Custom 3D-printed PR boxes and influencer packages: product-shaped, sculpted and engraved boxes for launches and seeding kits. Made in NYC.',
  eyebrow: 'PR boxes / 3D printed in New York',
  heading:
    'Custom 3D-printed PR boxes and influencer packages, shaped like your brand.',
  answer: `PrintAWorld 3D prints custom PR boxes in New York: influencer packages and seeding kits shaped like your product, your logo or your mascot, with fitted inserts and an engraved or printed lid. A 3D-printed box is something packaging companies don’t make, and creators film it. 3D modeling starts at ${modeling}, paid once per design; each box is ${quoted.toLowerCase()}.`,
  cta: 'Plan a PR box',
  heroImage: 'sculpted',
  ideaImages: ['productCase', 'engravedLid', 'character'],
  images: {
    sculpted: {
      file: 'sculpted-box',
      alt: 'Matte white 3D-printed pebble-shaped box with a skincare bottle seated in a fitted insert',
      caption: 'A sculpted box with a fitted insert',
    },
    productCase: {
      file: 'product-case',
      alt: 'Black 3D-printed case shaped like a sneaker sole, opened to show socks and a card',
      caption: 'A case in the shape of the product',
    },
    engravedLid: {
      file: 'engraved-lid',
      alt: 'Lilac 3D-printed box with a laser-engraved walnut lid and products in a fitted tray',
      caption: 'A printed box with an engraved wood lid',
    },
    character: {
      file: 'character-box',
      alt: 'Orange 3D-printed box shaped like a rounded mascot head, holding candy and a product',
      caption: 'A box shaped like your mascot',
    },
  },
  intro: {
    eyebrow: 'The box is the content',
    title: 'PR packages that look nothing like a printed box.',
    paragraphs: [
      'Most PR boxes are printed cardboard, and they all look alike in an unboxing video. A 3D-printed box can be any shape: the product, a logo, a character, a landmark. It is solid enough to keep on a shelf after the product is out.',
      'We model the box around your product, print it, paint it in your colors and add an engraved or UV-printed lid. Start with ten for top creators, or a few hundred for a launch.',
    ],
  },
  ideas: {
    eyebrow: 'PR box ideas',
    title: 'Influencer boxes shaped like the brand.',
  },
  uses: {
    eyebrow: 'Who they are for',
    title: 'PR packages for launches, seeding and press.',
    items: [
      {
        label: 'Beauty',
        title: 'Skincare and fragrance launches',
        body: 'A sculpted box with the bottle seated in a fitted insert.',
      },
      {
        label: 'Fashion',
        title: 'Sneaker and apparel drops',
        body: 'A case shaped like the product or the logo, for the first pairs out.',
      },
      {
        label: 'Food and drink',
        title: 'Seeding kits',
        body: 'A box that holds the product, a glass and a card, in the brand’s shape.',
      },
      {
        label: 'Tech and games',
        title: 'Press kits',
        body: 'A character or device-shaped case for review units and launch gifts.',
      },
      {
        label: 'Agencies',
        title: 'Campaign mailers',
        body: 'Run the box for every creator on the list, with names engraved if you want.',
      },
    ],
  },
  studio: {
    title: '3D printing and painting from our studio.',
    intro:
      'Painted and plated 3D-printed pieces made for brands. PR boxes are new for us; this is the same printing and finishing.',
    photos: [
      {
        url: `${assetRoot}${trophyPhotos.audible.file}`,
        alt: trophyPhotos.audible.alt,
        caption: 'Painted 3D-printed cubes',
      },
      {
        url: `${assetRoot}${trophyPhotos.wanba.file}`,
        alt: trophyPhotos.wanba.alt,
        caption: 'Painted 3D-printed characters',
      },
      {
        url: `${assetRoot}3E9CrRQJNhzgq4QTz0r5c2/b896cb34d6471434b1fabcda763151df/tiffany-co-display-3d-printed-and-metal-plated.jpg`,
        alt: '3D-printed and metal-plated retail display by PrintAWorld',
        caption: '3D printed and metal plated',
      },
    ],
  },
  materials: {
    link: 'How they’re made',
    eyebrow: 'How they’re made',
    title: 'Printed, painted, engraved and fitted.',
    items: [
      {
        title: 'The 3D-printed shell',
        body: 'Thick-walled, matte or glossy, in any shape, from a smooth pebble to a detailed character.',
      },
      {
        title: 'Paint and plating',
        body: 'Painted in your brand colors, or metal plated for a chrome or gold box.',
      },
      {
        title: 'Fitted inserts',
        body: 'A printed tray modeled around each product, so nothing rattles in the mail.',
      },
      {
        title: 'Engraved and printed lids',
        body: 'Walnut, acrylic or metal lids laser engraved, or your logo UV printed in color.',
      },
    ],
  },
  process: {
    title: 'From your product to a box creators keep.',
    note: 'Plan four to six weeks for modeling, a sample and the run. Tell us your send date.',
    steps: [
      {
        title: 'Send the product and idea',
        body: 'The products going in, a sketch or reference, your logo and the count.',
      },
      {
        title: 'Approve the 3D model',
        body: `We model the box and insert, paid once from ${modeling}, and send renders.`,
      },
      {
        title: 'Approve a sample',
        body: 'We print and finish one box so you can check the fit and the look.',
      },
      {
        title: 'We make the run',
        body: 'Every box is printed, finished and packed, ready for your fulfilment team.',
      },
    ],
  },
  pricing: {
    title: '3D-printed PR box pricing.',
    setupLabel: '3D modeling from',
    setup: modeling,
    setupNote: 'Paid once per design; every box after is printed and finished.',
    cta: 'Price my PR box',
    rows: [
      { label: 'Printing and finishing, per box', value: quoted },
      { label: 'Fitted inserts', value: quoted },
      { label: 'Engraved or printed lid', value: quoted },
      { label: 'Kitting and packing', value: quoted },
      { label: 'Rush', value: 'Case by case' },
    ],
  },
  bulk: {
    eyebrow: 'Seeding at scale',
    title: 'Ten boxes for top creators, or three hundred for a launch.',
    paragraphs: [
      'The model is paid once, so the price per box drops with every quantity. Ask for several quantities and we price each.',
      'Engrave each creator’s name on the lid, and the box becomes something they show on camera.',
    ],
    cta: 'Plan a launch',
  },
  reviews: ['3d-printing', 'custom-figures'],
  faqTitle: 'PR box questions, answered.',
  faqs: [
    {
      question: 'How much does a 3D-printed PR box cost?',
      answer: `3D modeling starts at ${modeling}, paid once per design. Printing, painting, inserts, lids and packing are ${quoted.toLowerCase()}, and the price per box drops with quantity. Ask for several quantities and we price each one.`,
    },
    {
      question: 'Can a PR box really be 3D printed?',
      answer:
        'Yes. We print thick-walled shells, lids and inserts in resin or plastic, then sand, paint or plate them. A printed box can be any shape, which a folded cardboard box can’t.',
    },
    {
      question: 'Is it strong enough to ship?',
      answer:
        'Boxes are modeled with thick walls and a fitted insert, then shipped inside a plain outer carton. We check the fit and the strength on the sample.',
    },
    {
      question: 'Can the box be shaped like our product or logo?',
      answer:
        'Yes. Send product photos or a 3D file and your logo. We model the box around them and send renders before printing.',
    },
    {
      question: 'What is the minimum order?',
      answer:
        'No minimum. Start with a sample and a small batch for your top creators.',
    },
    {
      question: 'How long does it take?',
      answer:
        'Plan four to six weeks for modeling, a sample and the run. Tell us your send date and we plan back from it.',
    },
    {
      question: 'Do you pack and ship to each creator?',
      answer: `We deliver finished boxes to your office or fulfilment partner, or for pickup in NYC. Kitting and mailing to each creator is ${quoted.toLowerCase()}.`,
    },
  ],
  quote: {
    title: 'Tell us about your PR box.',
    body: 'Send the products, an idea or reference, your logo, how many and your send date. We’ll reply with a concept and a written quote.',
    checklist: [
      'The products going in, with sizes',
      'A sketch, reference or the shape you want',
      'Your logo and brand colors',
      'How many, and your send date',
    ],
  },
  moreEyebrow: 'More for brand teams',
  moreTitle: 'Gifts and launches we make.',
  moreCards: ['bottleEngraving', 'customProps', 'corporateGifts'],
  related: [
    ['3D printing service', '/services/3d-printing/'],
    ['3D modeling', '/services/3d-modeling/'],
    ['Custom figurines', '/services/custom-figurines/'],
    ['Laser engraving', '/services/laser-engraving/'],
    ['UV printing', '/uv-printing-services/'],
    [
      'Beauty products for PR mailers',
      '/services/laser-engraving/beauty-products/',
    ],
  ],
  schema: {
    name: '3D-Printed PR Boxes',
    alternateName: [
      'Custom PR boxes',
      'Influencer boxes',
      'PR packages',
      '3D printed gift boxes',
    ],
    serviceType: 'Custom 3D-printed PR packaging',
    offer: {
      name: '3D modeling, from',
      price: String(prBoxPricing.modelingFrom),
    },
  },
}
